import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Loader2, Star, Users, Compass, X } from 'lucide-react';
import GoogleMapSearchBar, { LocationDetails } from '@/components/GoogleMapSearchBar';
import TourCreationProgress from './TourCreationProgress';
interface TourCreationModalProps {
  onClose: () => void;
  onTourCreated: (tour: any) => void;
  initialSearchText?: string;
}
interface TourFormData {
  city: string;
  country: string;
  duration: 'half-day' | 'full-day';
  style: 'must-do' | 'best-rated' | 'off-the-beaten-path';
  museum_count: number;
  latitude?: number;
  longitude?: number;
}
interface ExistingTourData {
  style: string;
  'Nom ville': string;
  country: string;
}
const TourCreationModal: React.FC<TourCreationModalProps> = ({
  onClose,
  onTourCreated,
  initialSearchText = ''
}) => {
  const {
    user
  } = useAuth();
  const [formData, setFormData] = useState<TourFormData>({
    city: '',
    country: '',
    duration: 'half-day',
    style: 'must-do',
    museum_count: 1
  });
  const [selectedLocation, setSelectedLocation] = useState<LocationDetails | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [existingTours, setExistingTours] = useState<ExistingTourData[]>([]);
  const [showProgress, setShowProgress] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [createdTourInfo, setCreatedTourInfo] = useState<{
    city: string;
    country: string;
    style: string;
  } | null>(null);
  const realtimeChannelRef = useRef<any>(null);
  const tourFoundRef = useRef(false);
  
  // Normalize DB style values (handle spaces vs dashes)
  const normalizeDbStyle = (s?: string) => (s || '').toLowerCase().replace(/\s+/g, '-');
  
  const styleOptions = [{
    value: 'must-do',
    label: 'Must Do',
    description: 'Iconic must-see landmarks',
    icon: <Star className="w-5 h-5" />
  }, {
    value: 'best-rated',
    label: 'Best Rated',
    description: 'Popular places with excellent reviews',
    icon: <Users className="w-5 h-5" />
  }, {
    value: 'off-the-beaten-path',
    label: 'Off the Beaten Path',
    description: 'Authentic local discoveries',
    icon: <Compass className="w-5 h-5" />
  }];
  const handleLocationSelect = async (location: LocationDetails) => {
    setSelectedLocation(location);
    setFormData(prev => ({
      ...prev,
      city: location.city,
      country: location.country,
      latitude: location.latitude,
      longitude: location.longitude
    }));

    // Check existing tours for this city
    try {
      // @ts-ignore - Complex type with quoted column name
      const { data, error } = await supabase
        .from('city_tours')
        .select('style, "Nom ville", country')
        .eq('"Nom ville"', location.city)
        .eq('country', location.country);
      
      if (error) throw error;
      setExistingTours((data || []) as ExistingTourData[]);
    } catch (error) {
      console.error('Error checking existing tours:', error);
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) {
      // Redirect to auth page with return URL
      window.location.href = '/auth?redirect=' + encodeURIComponent(window.location.pathname);
      return;
    }
    if (!selectedLocation) {
      toast.error('Please select a city');
      return;
    }

    // Check tour limits
    const cityTourCount = existingTours.length;
    if (cityTourCount >= 3) {
      toast.error(`This city has already reached the limit of 3 tours. Choose another destination.`);
      return;
    }

    // Check for duplicate style (normalize DB values)
    const existingStyle = existingTours.find(tour => normalizeDbStyle(tour.style) === formData.style);
    if (existingStyle) {
      const styleName = styleOptions.find(s => s.value === formData.style)?.label || formData.style;
      toast.error(`A "${styleName}" tour already exists for ${selectedLocation.city}. Choose another style.`);
      return;
    }
    setIsCreating(true);
    // Prepare progress and info immediately
    // reuse existing styleMap mapping above

    setCreatedTourInfo({
      city: selectedLocation.city,
      country: selectedLocation.country,
      style: formData.style // store DB-style (dash) value
    });

    // Show progress immediately while the function runs
    setShowProgress(true);

    try {
      // Call edge function to create tour and generate places/podcasts (runs while progress displays)
      const { data, error } = await supabase.functions.invoke('create-city-tour', {
        body: {
          city: selectedLocation.city, // Ville (ex: "Annecy")
          country: selectedLocation.country, // Pays (ex: "France")
          region: selectedLocation.region || selectedLocation.country, // Région (ex: "Haute-Savoie")
          duration: formData.duration, // "half-day" ou "full-day"
          style: formData.style, // "must-do", "best-rated", ou "off-the-beaten-path"
          museums_count: formData.museum_count,
          creator_user_id: user.id
        }
      });
      if (error) throw error;
      setIsCreating(false);
    } catch (error) {
      console.error('Error creating tour:', error);
      toast.error('Error creating tour');
      setShowProgress(false);
      setIsCreating(false);
    }
  };
  const nextStep = () => {
    if (currentStep < 2) {
      setCurrentStep(currentStep + 1);
    }
  };
  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };
  const canProceedToNextStep = () => {
    return selectedLocation !== null;
  };
  const isStyleDisabled = (styleValue: string) => {
    return existingTours.some(tour => normalizeDbStyle(tour.style) === styleValue);
  };

  // Set up Realtime listener for new tour insertions
  useEffect(() => {
    if (!showProgress || !createdTourInfo) return;

    console.log('🎧 Setting up Realtime listener for tour:', createdTourInfo);
    tourFoundRef.current = false;

    // Create realtime channel to listen for new tours
    const channel = supabase
      .channel('tour-creation-listener')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'city_tours',
          filter: `"Nom ville"=eq.${createdTourInfo.city}`
        },
        (payload) => {
          console.log('🔔 Realtime event received:', payload);
          
          const newTour = payload.new as any;
          
          // Check if this is the tour we're waiting for
          if (
            newTour['Nom ville'] === createdTourInfo.city &&
            newTour.country === createdTourInfo.country &&
            normalizeDbStyle(newTour.style) === formData.style &&
            newTour.creator_user_id === (user?.id || newTour.creator_user_id) &&
            !tourFoundRef.current
          ) {
            console.log('✅ Found our tour via Realtime!', newTour);
            tourFoundRef.current = true;
            handleTourFound(newTour);
          }
        }
      )
      .subscribe();

    realtimeChannelRef.current = channel;

    // Also poll as fallback in case Realtime doesn't work
    const pollForTour = async () => {
      let attempts = 0;
      const maxAttempts = 12; // 12 attempts x 10 seconds = 120 seconds (2 minutes) total
      
      while (attempts < maxAttempts && !tourFoundRef.current) {
        attempts++;
        console.log(`🔍 Polling attempt ${attempts}/${maxAttempts}`);
        
        const { data, error } = await supabase
          .from('city_tours')
          .select('*')
          .eq('"Nom ville"', createdTourInfo.city)
          .eq('country', createdTourInfo.country)
          .eq('style', formData.style)
          .eq('creator_user_id', user?.id || '')
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (data && !tourFoundRef.current) {
          console.log('✅ Tour found via polling:', data);
          tourFoundRef.current = true;
          handleTourFound(data);
          break;
        }

        if (error) {
          console.error('❌ Polling error:', error);
        }

        // Wait 10 seconds before next attempt
        await new Promise(resolve => setTimeout(resolve, 10000));
      }

      if (!tourFoundRef.current) {
        console.error('❌ Tour not found after all polling attempts');
        toast.error('Tour creation is taking longer than expected', {
          description: 'Please refresh the page in a moment to see your tour.'
        });
        setShowProgress(false);
        setIsCreating(false);
      }
    };

    pollForTour();

    // Cleanup
    return () => {
      if (realtimeChannelRef.current) {
        console.log('🧹 Cleaning up Realtime channel');
        supabase.removeChannel(realtimeChannelRef.current);
        realtimeChannelRef.current = null;
      }
    };
  }, [showProgress, createdTourInfo]);

  const handleTourFound = (foundTour: any) => {
    console.log('🎯 handleTourFound called with:', foundTour);
    
    // Cleanup Realtime channel immediately
    if (realtimeChannelRef.current) {
      supabase.removeChannel(realtimeChannelRef.current);
      realtimeChannelRef.current = null;
    }

    // Transform the tour data to match expected format
    const transformedTour = {
      id: foundTour.id,
      title: foundTour["Nom ville"] || foundTour.city || 'Tour sans nom',
      description: foundTour.description || '',
      city: foundTour["Nom ville"] || foundTour.city,
      country: foundTour.country,
      duration: foundTour.duration,
      style: foundTour.style,
      museum_count: foundTour.museum_count,
      latitude: foundTour.latitude,
      longitude: foundTour.longitude,
      created_at: foundTour.created_at,
      name_places: foundTour.name_places,
      Latitude_places: foundTour.Latitude_places,
      Longitude_places: foundTour.Longitude_places,
      image: foundTour.Image
    };
    
    console.log('📤 Calling onTourCreated with:', transformedTour);
    
    // Show success message before calling handlers
    toast.success('🎉 Your tour is ready!', {
      description: 'Zooming to your new tour...'
    });
    
    // Call onTourCreated which will handle the map zoom and popup
    onTourCreated(transformedTour);
    
    console.log('🚪 Closing modal...');
    onClose();
  };

  const handleProgressComplete = async () => {
    console.log('🎯 handleProgressComplete called - progress animation done');
    // The tour detection is now handled by the useEffect with Realtime + polling
    // This function is just called when the progress bar animation completes
    // If tour hasn't been found yet, polling will continue
  };
  return <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-xs w-full p-0 gap-0 bg-white/90 backdrop-blur-md mx-auto rounded-2xl border-0 shadow-2xl left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
        {/* Progress Screen */}
        {showProgress && (
          <div className="flex flex-col h-[400px]">
            <TourCreationProgress onComplete={handleProgressComplete} isRetrying={isRetrying} />
          </div>
        )}

        {/* Page 1: Ville + Paramètres */}
        {!showProgress && currentStep === 1 && <div className="flex flex-col h-[65vh] max-h-[450px]">
            {/* Header */}
            <div className="flex items-center justify-between p-5 pb-3 flex-shrink-0">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center">
                    <span className="text-white text-xs">🗺️</span>
                  </div>
                  <h2 className="text-lg font-medium text-gray-900">Create a tour</h2>
                </div>
                <p className="text-xs text-gray-500">Step 1/2</p>
              </div>
              <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-full transition-colors">
                <X className="w-4 h-4 text-gray-400" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 px-5 space-y-4 overflow-y-auto">
              {/* Bloc 1 - Recherche de ville */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">Search for a place</h3>
                <GoogleMapSearchBar onLocationSelect={handleLocationSelect} selectedLocation={selectedLocation} placeholder="e.g. Paris, London…" initialValue={initialSearchText} />
              </div>

              {/* Bloc 2 - Durée de visite */}
              <div>
                <div className="flex gap-2">
                  <button type="button" onClick={() => setFormData(prev => ({
                ...prev,
                duration: 'half-day'
              }))} className={`flex-1 py-3 px-4 rounded-2xl border-2 transition-all text-sm font-medium ${formData.duration === 'half-day' ? 'bg-blue-500 border-blue-500 text-white shadow-lg' : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300'}`}>
                    Half-day
                  </button>
                  <button type="button" onClick={() => setFormData(prev => ({
                ...prev,
                duration: 'full-day'
              }))} className={`flex-1 py-3 px-4 rounded-2xl border-2 transition-all text-sm font-medium ${formData.duration === 'full-day' ? 'bg-blue-500 border-blue-500 text-white shadow-lg' : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300'}`}>
                    Full-day
                  </button>
                </div>
              </div>

            </div>

            {/* Action en bas */}
            <div className="p-5 pt-3 flex-shrink-0">
              <Button onClick={nextStep} disabled={!canProceedToNextStep()} className={`w-full py-3 text-white font-medium rounded-2xl transition-all ${canProceedToNextStep() ? 'bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 shadow-lg' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}>
                Next
              </Button>
            </div>
          </div>}

        {/* Page 2: Style de visite */}
        {!showProgress && currentStep === 2 && <div className="flex flex-col h-[65vh] max-h-[450px]">
            {/* Header avec résumé */}
            <div className="flex flex-col items-center justify-center p-5 pb-4 flex-shrink-0">
              <h2 className="text-lg font-medium text-gray-900 mb-3">Tour style</h2>
              <div className="flex gap-2 justify-center flex-wrap">
                <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-rose-100 text-rose-700">
                  {selectedLocation?.city}
                </span>
                <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-sky-100 text-sky-700">
                  {formData.duration === 'half-day' ? 'Half-day' : 'Full-day'}
                </span>
              </div>
              <button onClick={onClose} className="absolute top-5 right-5 p-1.5 hover:bg-gray-100 rounded-full transition-colors">
                <X className="w-4 h-4 text-gray-400" />
              </button>
            </div>

            {/* Section choix du style */}
            <div className="flex-1 px-5 space-y-3 overflow-y-auto">
              {styleOptions.map(option => {
            const disabled = isStyleDisabled(option.value);
            const selected = formData.style === option.value;
            return <button key={option.value} type="button" disabled={disabled} onClick={() => !disabled && setFormData(prev => ({
              ...prev,
              style: option.value as any
            }))} className={`w-full p-4 rounded-2xl border-2 transition-all text-left ${disabled ? 'bg-gray-50 border-gray-200 opacity-50 cursor-not-allowed' : selected ? 'bg-blue-50 border-blue-500 shadow-lg' : 'bg-white border-gray-200 hover:border-gray-300'}`}>
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-xl ${disabled ? 'bg-gray-200 text-gray-400' : selected ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'}`}>
                        {option.icon}
                      </div>
                      <div>
                        <h3 className={`font-medium text-sm ${disabled ? 'text-gray-400' : selected ? 'text-blue-900' : 'text-gray-900'}`}>
                          {option.label}
                          {disabled && <span className="text-xs ml-2">(Already created)</span>}
                        </h3>
                        <p className={`text-xs mt-1 ${disabled ? 'text-gray-400' : 'text-gray-500'}`}>
                          {option.description}
                        </p>
                      </div>
                    </div>
                  </button>;
          })}
            </div>

            {/* CTA final */}
            <div className="p-5 pt-3 flex-shrink-0">
              <div className="flex gap-2">
                <Button onClick={prevStep} variant="outline" className="flex-1 py-3 border-2 border-gray-200 text-gray-700 hover:bg-gray-50 rounded-2xl">
                  Previous
                </Button>
                <Button onClick={handleSubmit} disabled={isCreating} className="flex-2 py-3 bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 text-white font-medium rounded-2xl shadow-lg">
                  {isCreating ? <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creating...
                    </> : 'Create a tour'}
                </Button>
              </div>
            </div>
          </div>}
      </DialogContent>
    </Dialog>;
};
export default TourCreationModal;